import React from "react";

/**
 * The one sentence at the top of an Analytics panel saying what it currently
 * means, with the evidence unchanged directly beneath it.
 *
 * THREE RULES, and they are what keep this honest rather than clever.
 *
 * 1. EVERY FIGURE IN A HEADLINE MUST ALSO APPEAR IN THE EVIDENCE BELOW IT.
 *    A headline is a reading of the table, never a separate claim the operator
 *    cannot check.
 *
 * 2. A FAILED FETCH OR AN EMPTY WINDOW SAYS SO, IN WORDS. It never narrates a
 *    number it does not have. ChatConversionStats used to leave both cards
 *    reading 0% when its request threw, so a broken endpoint looked exactly
 *    like a real business number.
 *
 * 3. THE HEADLINE IS A <p>, NOT A HEADING. The six stable <h5> panel names are
 *    the page's outline for keyboard and screen-reader navigation; this
 *    sentence changes every day and must not compete with them.
 */
const PanelHeadline = ({ failed, failedText, empty, emptyText, children }) => {
  if (failed) {
    return (
      <p
        className="mb-3 px-12 py-12 radius-8 text-danger-600"
        style={{
          background: "rgba(192, 57, 43, 0.08)",
          borderLeft: "3px solid #c0392b",
          borderRadius: "0 8px 8px 0",
          fontSize: "1rem",
          lineHeight: 1.45,
          maxWidth: "48ch",
        }}
        role="status"
      >
        <strong className="d-block">{failedText}</strong>
        Nothing below is a business number until this loads.
      </p>
    );
  }
  if (empty) {
    return (
      <p className="mb-3 text-secondary-light" style={{ fontSize: "1rem", maxWidth: "48ch" }}>
        {emptyText}
      </p>
    );
  }
  return (
    <p
      className="mb-3 text-primary-light"
      style={{ fontSize: "1.25rem", lineHeight: 1.4, maxWidth: "44ch", textWrap: "balance" }}
    >
      {children}
    </p>
  );
};

/** A number inside a headline: heavier, and lined up if it sits near others. */
export const Fig = ({ children }) => (
  <span className="fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
    {children}
  </span>
);

/**
 * A comparison chip that always NAMES what it is comparing against.
 *
 * 🔴 ABSOLUTE CHANGE, NEVER PERCENT, AND NOTHING AT ALL ON A TINY BASE.
 * On a pocket this size a move from 2 to 3 is a 50% rise and means nothing;
 * printing that on a daily read is how a false alarm starts. Below `floor` the
 * chip does not render.
 */
export const Delta = ({ now, prev, label, floor = 5, goodIsUp = true }) => {
  if (prev === null || prev === undefined || !Number.isFinite(prev)) return null;
  if (Math.max(now, prev) < floor) return null;
  if (now === prev) {
    return (
      <span className="ms-2 px-8 py-2 radius-4 text-xs fw-semibold bg-neutral-200 text-secondary-light">
        no change {label}
      </span>
    );
  }
  const up = now > prev;
  const good = up === goodIsUp;
  return (
    <span
      className={
        "ms-2 px-8 py-2 radius-4 text-xs fw-semibold " +
        (good ? "bg-success-100 text-success-600" : "bg-danger-100 text-danger-600")
      }
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {up ? "up" : "down"} from {prev} {label}
    </span>
  );
};

export default PanelHeadline;
